/*
  App-free wishlist: products are stored in this browser's localStorage and kept in
  sync across every heart, the header count and the wishlist page via pubsub.js
  (publish/subscribe, loaded before this file from snippets/head-scripts.liquid).

  Ported from the Cooks Midland (Dawn) implementation; Athens-specific changes:
  - Add to cart hands off to the theme's <mini-cart> (renderContents + open), the same
    flow as assets/product-form.js, instead of Dawn's cart-drawer / cart-notification.
  - Items carry a `purchasable` flag so products with hidden pricing (call for pricing /
    login for price) never show a price or an Add to cart button on the wishlist page.
  - Every stored value is escaped before going into innerHTML.
*/

const WISHLIST_STORAGE_KEY = 'wat_part_wishlist';
const WISHLIST_UPDATED_EVENT = 'wishlist:updated';

class Wishlist {
  static getItems() {
    try {
      const stored = localStorage.getItem(WISHLIST_STORAGE_KEY);
      const items = stored ? JSON.parse(stored) : [];
      return Array.isArray(items) ? items : [];
    } catch (error) {
      return [];
    }
  }

  static saveItems(items) {
    try {
      localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('Wishlist: could not save to localStorage', error);
    }
    this.updateCountBadges();
    publish(WISHLIST_UPDATED_EVENT, { items, count: items.length });
  }

  static has(productId) {
    return this.getItems().some((item) => String(item.id) === String(productId));
  }

  static add(product) {
    const items = this.getItems();
    if (this.has(product.id)) return items;

    const nextItems = [
      {
        id: String(product.id),
        handle: product.handle,
        title: product.title,
        url: product.url,
        image: product.image || '',
        price: product.price || '',
        variantId: product.variantId ? String(product.variantId) : '',
        purchasable: product.purchasable !== false,
        singleVariant: product.singleVariant !== false,
        available: product.available,
        addedAt: Date.now(),
      },
      ...items,
    ];

    this.saveItems(nextItems);
    return nextItems;
  }

  static remove(productId) {
    const nextItems = this.getItems().filter((item) => String(item.id) !== String(productId));
    this.saveItems(nextItems);
    return nextItems;
  }

  static toggle(product) {
    if (this.has(product.id)) {
      return this.remove(product.id);
    }
    return this.add(product);
  }

  static getCount() {
    return this.getItems().length;
  }

  static updateCountBadges() {
    const count = this.getCount();
    document.querySelectorAll('[data-wishlist-count]').forEach((element) => {
      element.textContent = count;
    });

    document.querySelectorAll('[data-wishlist-count-label]').forEach((element) => {
      const template = window.wishlistStrings?.count || 'Items in wishlist: [count]';
      element.textContent = template.replace('[count]', count);
    });

    document.querySelectorAll('[data-wishlist-count-wrapper]').forEach((element) => {
      element.classList.toggle('wishlist-count-bubble--empty', count < 1);
    });
  }
}

function wishlistEscape(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

class WishlistButton extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onWishlistUpdated = this.onWishlistUpdated.bind(this);
  }

  connectedCallback() {
    this.trigger = this.querySelector('[data-wishlist-trigger]') || this.querySelector('button');
    if (!this.trigger) return;

    this.product = {
      id: this.dataset.productId,
      handle: this.dataset.productHandle,
      title: this.dataset.productTitle,
      url: this.dataset.productUrl,
      image: this.dataset.productImage,
      price: this.dataset.productPrice,
      variantId: this.dataset.variantId,
      purchasable: this.dataset.productPurchasable !== 'false',
      singleVariant: this.dataset.productSingleVariant !== 'false',
      available: this.dataset.productAvailable !== 'false',
    };

    this.trigger.addEventListener('click', this.onClick);
    this.unsubscribe = subscribe(WISHLIST_UPDATED_EVENT, this.onWishlistUpdated);
    this.updateState();
  }

  disconnectedCallback() {
    if (this.trigger) this.trigger.removeEventListener('click', this.onClick);
    if (this.unsubscribe) this.unsubscribe();
  }

  onWishlistUpdated() {
    this.updateState();
  }

  updateState() {
    const isActive = Wishlist.has(this.product.id);
    this.classList.toggle('is-active', isActive);

    const addLabel = this.dataset.labelAdd || 'Add to wishlist';
    const removeLabel = this.dataset.labelRemove || 'Remove from wishlist';
    this.trigger.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    this.trigger.setAttribute('aria-label', isActive ? removeLabel : addLabel);
  }

  onClick(event) {
    // The media-style heart sits over the product card's image link.
    event.preventDefault();
    event.stopPropagation();

    const wasActive = Wishlist.has(this.product.id);
    Wishlist.toggle(this.product);

    if (!wasActive && window.wishlistStrings?.added) {
      this.showMessage(window.wishlistStrings.added);
    }
  }

  showMessage(message) {
    const liveRegion = document.getElementById('wishlist-live-region');
    if (!liveRegion) return;
    liveRegion.textContent = message;
    window.setTimeout(() => {
      liveRegion.textContent = '';
    }, 3000);
  }
}

class WishlistPage extends HTMLElement {
  constructor() {
    super();
    this.onWishlistUpdated = this.onWishlistUpdated.bind(this);
    this.onGridClick = this.onGridClick.bind(this);
    // Live stock per product id for this page view: 'in_stock' | 'out_of_stock' | 'unavailable'.
    this.stock = new Map();
    this.stockRequests = new Map();
  }

  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    if (this.grid) this.grid.addEventListener('click', this.onGridClick);
    this.unsubscribe = subscribe(WISHLIST_UPDATED_EVENT, this.onWishlistUpdated);
    this.render();
  }

  disconnectedCallback() {
    if (this.grid) this.grid.removeEventListener('click', this.onGridClick);
    if (this.unsubscribe) this.unsubscribe();
  }

  onWishlistUpdated() {
    this.render();
  }

  render() {
    const items = Wishlist.getItems();
    Wishlist.updateCountBadges();

    if (!this.grid || !this.empty) return;

    this.empty.hidden = items.length > 0;
    this.grid.hidden = items.length === 0;
    this.grid.innerHTML = items.map((item) => this.renderCard(item)).join('');
    this.refreshStock(items);
  }

  // Stock saved with the item is only a snapshot, so re-check every item against the
  // storefront once per page view and update its card in place.
  refreshStock(items) {
    items.forEach((item) => {
      if (!item.handle || this.stock.has(item.id) || this.stockRequests.has(item.id)) return;

      const request = this.fetchStock(item)
        .then((status) => {
          this.stock.set(item.id, status);
          this.updateCardStock(item);
        })
        .catch((error) => console.error('Wishlist: could not check stock', error))
        .finally(() => this.stockRequests.delete(item.id));
      this.stockRequests.set(item.id, request);
    });
  }

  async fetchStock(item) {
    // Relative to the current storefront (not window.shopUrl), so it also works on
    // previews served from the myshopify domain.
    const root = (window.routes?.root_url || '/').replace(/\/$/, '');
    const response = await fetch(`${root}/products/${encodeURIComponent(item.handle)}.js`);
    if (response.status === 404) return 'unavailable';
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const product = await response.json();
    const variant = product.variants.find((entry) => String(entry.id) === String(item.variantId));
    const available = item.singleVariant !== false && variant ? variant.available : product.available;
    return available ? 'in_stock' : 'out_of_stock';
  }

  getStockStatus(item) {
    if (this.stock.has(item.id)) return this.stock.get(item.id);
    if (item.available === true) return 'in_stock';
    if (item.available === false) return 'out_of_stock';
    return null;
  }

  updateCardStock(item) {
    const card = this.grid?.querySelector(`[data-wishlist-item="${CSS.escape(String(item.id))}"]`);
    if (!card) return;
    const template = document.createElement('template');
    template.innerHTML = this.renderCard(item).trim();
    card.replaceWith(template.content.firstElementChild);
  }

  renderStock(status) {
    if (!status) return '';
    const strings = window.wishlistStrings || {};
    const labels = {
      in_stock: strings.inStock || 'In stock',
      out_of_stock: strings.outOfStock || 'Out of stock',
      unavailable: strings.unavailable || 'No longer available',
    };
    // Same badge markup as snippets/product-availability.liquid, so it matches the PDP.
    const badgeClass = status === 'in_stock' ? 'badge-stock-in' : 'badge-stock-out';
    return `
      <div class="wishlist-page__stock">
        <div class="badge-stock ${badgeClass}">
          <span class="badge-stock-dot"></span>
          <span>${wishlistEscape(labels[status])}</span>
        </div>
      </div>
    `;
  }

  renderCard(item) {
    const strings = window.wishlistStrings || {};
    const url = wishlistEscape(item.url);
    const title = wishlistEscape(item.title);
    const purchasable = item.purchasable !== false;
    const status = this.getStockStatus(item);
    const soldOut = status === 'out_of_stock' || status === 'unavailable';
    // Multi-variant products go to the product page to pick an option rather than
    // adding whichever variant happened to be selected when the item was saved.
    const canAddToCart = purchasable && item.singleVariant !== false;

    return `
      <article class="wishlist-page__card${soldOut ? ' is-sold-out' : ''}" data-wishlist-item="${wishlistEscape(item.id)}">
        <div class="wishlist-page__media">
          ${item.image ? `<a href="${url}"><img src="${wishlistEscape(item.image)}" alt="${title}" loading="lazy"></a>` : ''}
          <button
            type="button"
            class="wishlist-page__remove"
            data-remove-id="${wishlistEscape(item.id)}"
            aria-label="${wishlistEscape(strings.remove || 'Remove from wishlist')}"
          >
            ${window.wishlistIcons?.heartFilled || ''}
          </button>
        </div>
        <div class="wishlist-page__content">
          <h3 class="wishlist-page__title h6"><a href="${url}">${title}</a></h3>
          ${purchasable && item.price ? `<p class="wishlist-page__price">${wishlistEscape(item.price)}</p>` : ''}
          ${this.renderStock(status)}
          <div class="wishlist-page__actions">
            <a href="${url}" class="button">${wishlistEscape(strings.viewProduct || 'View product')}</a>
            ${
              canAddToCart
                ? `<button
                    type="button"
                    class="button button-secondary"
                    data-add-to-cart
                    data-product-id="${wishlistEscape(item.id)}"
                    data-handle="${wishlistEscape(item.handle)}"
                    data-variant-id="${wishlistEscape(item.variantId)}"
                    ${soldOut ? 'disabled' : ''}
                  >
                    <span data-add-to-cart-label>${wishlistEscape(
                      soldOut ? strings.outOfStock || 'Out of stock' : strings.addToCart || 'Add to cart'
                    )}</span>
                    <div class="button-overlay-spinner hidden">
                      <span class="spinner-xs"></span>
                    </div>
                  </button>`
                : ''
            }
          </div>
        </div>
      </article>
    `;
  }

  onGridClick(event) {
    const removeButton = event.target.closest('[data-remove-id]');
    if (removeButton) {
      Wishlist.remove(removeButton.dataset.removeId);
      return;
    }

    const addButton = event.target.closest('[data-add-to-cart]');
    if (addButton) this.addToCart(addButton);
  }

  async resolveVariantId(button) {
    if (button.dataset.variantId) return button.dataset.variantId;
    if (!button.dataset.handle) return null;

    // Relative to the current storefront (not window.shopUrl), so it also works on
    // previews served from the myshopify domain.
    const root = (window.routes?.root_url || '/').replace(/\/$/, '');
    const response = await fetch(`${root}/products/${encodeURIComponent(button.dataset.handle)}.js`);
    if (!response.ok) throw new Error('Product not found');
    const product = await response.json();
    const availableVariant = product.variants.find((variant) => variant.available);
    return availableVariant?.id || product.variants[0]?.id || null;
  }

  // Mirrors assets/product-form.js: add via routes.cart_add_url, then hand the
  // response to the page's <mini-cart> so the site-wide cart drawer updates and opens.
  async addToCart(button) {
    if (button.disabled || button.classList.contains('loading')) return;
    const strings = window.wishlistStrings || {};
    const productId = button.dataset.productId;
    const label = button.querySelector('[data-add-to-cart-label]');

    button.classList.add('loading');
    button.setAttribute('aria-disabled', 'true');

    try {
      const variantId = await this.resolveVariantId(button);
      if (!variantId) throw new Error('Variant not found');

      const minicart = document.querySelector('mini-cart');
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      if (minicart) {
        formData.append('sections', minicart.getSectionsToRender().map((section) => section.section));
        formData.append('sections_url', window.location.pathname);
      }

      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const data = await response.json();

      if (data.status) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'wishlist',
          productVariantId: variantId,
          errors: data.errors || data.description,
          message: data.message,
        });
        throw new Error(data.description || data.message || 'Could not add to cart');
      }

      publish(PUB_SUB_EVENTS.cartItemAdd, { source: 'wishlist', items: [data] });

      if (!minicart || window.themeSettings?.redirectToCart) {
        if (productId) Wishlist.remove(productId);
        window.location = window.routes.cart_url;
        return;
      }

      minicart.renderContents(data);
      minicart.open(button);

      if (productId) Wishlist.remove(productId);
    } catch (error) {
      console.error('Wishlist: add to cart failed', error);
      if (label && strings.addToCartError) {
        label.textContent = strings.addToCartError;
        window.setTimeout(() => {
          label.textContent = strings.addToCart || 'Add to cart';
        }, 1800);
      }
    } finally {
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
    }
  }
}

customElements.define('wishlist-button', WishlistButton);
customElements.define('wishlist-page', WishlistPage);

// Keep other open tabs in sync when the list changes in this one.
window.addEventListener('storage', (event) => {
  if (event.key !== WISHLIST_STORAGE_KEY) return;
  const items = Wishlist.getItems();
  Wishlist.updateCountBadges();
  publish(WISHLIST_UPDATED_EVENT, { items, count: items.length });
});

// Deferred script: the DOM is already parsed, so the header badge can be set now.
Wishlist.updateCountBadges();
